import { h } from 'preact';
import UIElement from '../internal/UIElement/UIElement';
import ANCVInput from './components/ANCVInput';
import config from './components/ANCVAwait/config';
import { Await } from '../../components/internal/Await';
import AdyenCheckoutError from '../../core/Errors/AdyenCheckoutError';
import PayButton from '../internal/PayButton';
import { ANCVConfiguration } from './types';
import { sanitizeResponse, verifyPaymentDidNotFail } from '../internal/UIElement/utils';
import { PayButtonProps } from '../internal/PayButton/PayButton';

export class ANCVElement extends UIElement<ANCVConfiguration> {
    public static readonly type = 'ancv';

    /**
     * Formats the component data output
     */
    formatData() {
        return {
            paymentMethod: {
                type: ANCVElement.type,
                beneficiaryId: this.state.data?.beneficiaryId
            }
        };
    }

    private onOrderRequest = data => {
        if (this.props.onOrderRequest)
            return new Promise((resolve, reject) => {
                void this.props.onOrderRequest(resolve, reject, data);
            });

        if (this.props.session) {
            return this.props.session.createOrder();
        }
    };

    /**
     * Check if order exists, if it does Resolve.
     * Otherwise createOrder and then Resolve.
     */
    public createOrder = (): Promise<void> => {
        this.setStatus('loading');

        // allow for multiple ANCV payments, follow giftcard logic and just use order if it exists
        if (this.props.order) {
            return Promise.resolve();
        }

        return this.onOrderRequest(this.data)
            .then((order: { orderData: string; pspReference: string }) => {
                const stateOrder = { order: { orderData: order.orderData, pspReference: order.pspReference } };
                this.setState(stateOrder);
                return Promise.resolve();
            })
            .catch(error => {
                if (error instanceof AdyenCheckoutError) {
                    this.handleError(error);
                } else {
                    this.handleError(new AdyenCheckoutError('ERROR', error));
                }
            });
    };

    public submit() {
        if (!this.isValid) {
            this.showValidation();
            return false;
        }

        this.createOrder()
            .then(this.makePaymentsCall)
            .then(sanitizeResponse)
            .then(verifyPaymentDidNotFail)
            .then(this.handleResponse)
            .catch(this.handleFailedResult);
    }

    // Reimplement payButton similar to GiftCard to allow to set onClick
    protected override payButton = (props: PayButtonProps): h.JSX.Element => {
        return <PayButton {...props} />;
    };

    get isValid(): boolean {
        return !!this.state.isValid;
    }

    get displayName(): string {
        return this.props.name;
    }

    protected override componentToRender(): h.JSX.Element {
        if (this.props.paymentData) {
            return (
                <Await
                    clientKey={this.props.clientKey}
                    paymentData={this.props.paymentData}
                    onError={this.props.onError}
                    onComplete={this.onComplete}
                    brandLogo={this.icon}
                    brandName={this.displayName}
                    type={this.constructor['type']}
                    messageText={this.props.i18n.get('ancv.confirmPayment')}
                    awaitText={this.props.i18n.get('await.waitForConfirmation')}
                    showCountdownTimer={config.showCountdownTimer}
                    throttleTime={config.THROTTLE_TIME}
                    throttleInterval={config.THROTTLE_INTERVAL}
                    onActionHandled={this.onActionHandled}
                />
            );
        }

        return (
            <ANCVInput
                {...this.props}
                setComponentRef={this.setComponentRef}
                onSubmit={this.submit}
                onChange={this.setState}
                payButton={this.payButton}
                showPayButton={this.props.showPayButton}
            />
        );
    }
}

export default ANCVElement;
